import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, FileUp, Check, AlertTriangle, FileText, ShieldCheck } from "lucide-react";

export default function ImportWord() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [ext, setExt] = useState(null);
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState("");
  const [narr, setNarr] = useState({});
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy("extract");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/harmonized/entry/import-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExt(r.data);
      setRows(r.data.rows.map((x) => ({ ...x, value: String(x.value), original: String(x.value), confirmed: false })));
      setDate(r.data.date || "");
      setNarr(r.data.narratives || {});
      setResult(null);
      toast.success(`${r.data.rows.length} ${t("valeurs extraites. Vérifiez chaque ligne avant d'enregistrer.")}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || t("Import impossible.")); } finally { setBusy(""); }
  };

  const setRow = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const confirmed = rows.filter((r) => r.confirmed && r.value !== "");
  const allChecked = rows.length > 0 && rows.every((r) => r.confirmed || r.value === "");

  const commit = async () => {
    setBusy("commit");
    try {
      const r = await api.post("/harmonized/entry/import-commit", {
        date, values: Object.fromEntries(confirmed.map((x) => [x.code, x.value])), narratives: narr, areas: ext.areas,
        file_path: ext.file_path, filename: ext.filename, corrected: confirmed.filter((x) => x.value !== x.original).map((x) => x.code),
      });
      setResult(r.data);
      toast.success(t("Saisie enregistrée à partir du rapport importé."));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="import-word-page">
      <PageHeader eyebrow={t("Rapports existants")} title={t("Importer un rapport Word")}
        subtitle={t("Déposez votre rapport habituel (Word ou PDF). Les chiffres trouvés s'affichent dans un tableau : vous confirmez ou corrigez chaque valeur avant l'enregistrement. Le document original est conservé comme pièce justificative.")} testid="import-word-header" />

      <label className={`flex items-center justify-center gap-2 h-24 rounded-lg border-2 border-dashed text-sm font-medium cursor-pointer mb-4 ${busy ? "border-slate-200 text-slate-400" : "border-[#F05A28] text-[#F05A28] hover:bg-[#FFF3EE]"}`} data-testid="import-word-drop">
        {busy === "extract" ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileUp className="h-5 w-5" />}
        {busy === "extract" ? t("Lecture du document…") : t("Choisir un fichier .docx ou .pdf")}
        <input type="file" accept=".docx,.doc,.pdf,.txt" className="hidden" disabled={!!busy} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="import-word-input" />
      </label>

      {ext && !result && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 flex flex-wrap items-end gap-4">
            <div><p className="text-xs text-slate-500 mb-1">{t("Document")}</p><p className="text-sm font-medium inline-flex items-center gap-1.5" data-testid="import-word-filename"><FileText className="h-4 w-4 text-slate-400" />{ext.filename}</p></div>
            <div><p className="text-xs text-slate-500 mb-1">{t("Date du rapport")}</p><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 w-44" data-testid="import-word-date" /></div>
            <div className="ml-auto text-xs text-slate-500" data-testid="import-word-progress">{confirmed.length}/{rows.length} {t("valeurs confirmées")}</div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="import-word-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b"><th className="p-3">{t("Indicateur")}</th><th className="p-3">{t("Valeur lue")}</th><th className="p-3">{t("Valeur à enregistrer")}</th><th className="p-3">{t("Vérification")}</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.code} className={`border-b border-slate-100 ${r.confirmed ? "bg-emerald-50/40" : r.uncertain ? "bg-amber-50/60" : ""}`} data-testid={`import-row-${r.code}`}>
                    <td className="p-3"><b className="text-slate-800">{r.label}</b><div className="font-mono text-[11px] text-slate-400">{r.code}</div></td>
                    <td className="p-3 text-slate-600">{r.original}{r.uncertain && <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-amber-700 font-semibold" data-testid={`import-uncertain-${r.code}`}><AlertTriangle className="h-3 w-3" />{t("ambigu dans le texte")}</span>}</td>
                    <td className="p-3"><Input type="number" value={r.value} onChange={(e) => setRow(i, { value: e.target.value, confirmed: false })} className="h-9 w-32" data-testid={`import-value-${r.code}`} /></td>
                    <td className="p-3">
                      <button type="button" onClick={() => setRow(i, { confirmed: !r.confirmed })} className={`inline-flex items-center gap-1.5 text-xs rounded-md px-2.5 py-1.5 border ${r.confirmed ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`} data-testid={`import-confirm-${r.code}`}>
                        <Check className="h-3.5 w-3.5" />{r.confirmed ? (r.value !== r.original ? t("Corrigée") : t("Confirmée")) : t("Confirmer")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <p className="p-6 text-sm text-slate-500 italic" data-testid="import-word-none">{t("Aucun chiffre reconnu dans ce document.")}</p>}
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4" data-testid="import-word-narratives">
            <h2 className="text-sm font-semibold text-slate-900 mb-2">{t("Textes repris du rapport")}</h2>
            {Object.keys(narr).length === 0 && <p className="text-xs text-slate-400 italic">{t("Aucun texte repris.")}</p>}
            {Object.entries(narr).map(([k, v]) => (
              <div key={k} className="mb-2"><p className="text-[11px] font-mono text-slate-400">{k}</p><textarea value={v} onChange={(e) => setNarr({ ...narr, [k]: e.target.value })} rows={3} className="w-full text-sm rounded-md border border-slate-200 p-2" data-testid={`import-narr-${k}`} /></div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={commit} disabled={!!busy || !date || !allChecked || confirmed.length === 0} className="h-11 rounded-md bg-[#0F172A] hover:bg-[#1E293B]" data-testid="import-word-commit">
              {busy === "commit" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}{t("Enregistrer les valeurs confirmées")}
            </Button>
            {!allChecked && rows.length > 0 && <span className="text-xs text-slate-500" data-testid="import-word-hint">{t("Confirmez (ou videz) chaque ligne pour pouvoir enregistrer.")}</span>}
          </div>
        </div>
      )}

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5" data-testid="import-word-result">
          <p className="text-sm font-semibold text-emerald-800">{t("Saisie du")} {result.date} {t("enregistrée. Document original conservé, validation tracée à votre nom.")}</p>
          {result.dq_flags?.length > 0 && <p className="text-xs text-amber-800 mt-2" data-testid="import-word-flags">{result.dq_flags.length} {t("point(s) de qualité à vérifier par le superviseur :")} {result.dq_flags.map((f) => f.message).join(" · ")}</p>}
          <div className="mt-3 flex gap-2">
            <Button variant="outline" onClick={() => navigate("/entry")} data-testid="import-word-open-entry">{t("Ouvrir la saisie")}</Button>
            <Button variant="ghost" onClick={() => { setExt(null); setResult(null); setRows([]); }} data-testid="import-word-again">{t("Importer un autre rapport")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
